import type { Lang } from "../i18n/i18n";
import { textFor } from "../i18n/i18n";
import { formatMoney } from "../units/format";
import type { UnitsSettings } from "../units/units";
import type { TemplateVariable } from "./templateVariables";

export type TemplateVariablePreviewValueSet = Record<string, string>;
export type TemplateVariablePreviewOptions = {
  renderLinks?: boolean;
};

const DATE_PART_WIDTH = 2;
const DEFAULT_FRACTION_DIGITS = 2;
const MAX_SOURCE_DECIMAL_PLACES = 4;
const DAYS_PER_WEEK = 7;
const PERCENT_SCALE = 100;
const REPEATED_TEXT_COPIES = 2;
const CALC_OP_ADD = "+";
const CALC_OP_SUBTRACT = "-";
const CALC_OP_MULTIPLY = "*";
const CALC_OP_DIVIDE = "/";
const DATE_UNIT_DAY = "d";
const DATE_UNIT_WEEK = "w";
const DATE_UNIT_MONTH = "m";
const DATE_UNIT_YEAR = "y";
const LINE_BREAK_LF = "\n";
/** SFDT-Zeichenformat und Farbe, an denen ein Link-Textlauf erkannt wird. */
const SFDT_UNDERLINE_SINGLE = "Single";
const SFDT_LINK_FONT_COLOR = "#0563c1";
const SFDT_INLINES_KEY = "inlines";

const TEMPLATE_LINK_TOKEN_REGEX = /(\{\{\s*)([A-Za-z][A-Za-z0-9_]*)\s+link(?:\s*=\s*(?:"([^"]+)"|'([^']+)'|&quot;(.+?)&quot;))?(\s*\}\})/gi;

/**
 * Die Beispielbeträge als Zahlen. Geschrieben werden sie erst beim Zusammenstellen — mit demselben
 * Helfer und derselben Einstellung (Einheiten) wie das fertige Schreiben. Als feste Zeichenketten
 * gepflegt zeigten sie eine Schreibweise, die das erzeugte Dokument nie hatte: „€718.80" in der
 * Leiste, „718,80 €" im Brief.
 */
const TEMPLATE_VARIABLE_PREVIEW_AMOUNTS: Record<string, number> = {
  contractPrice: 59.9,
  contractMainTermTotalAmount: 718.8,
  contractPaidAmount: 359.4,
  contractRemainingAmount: 359.4,
  restPeriodMonthlyFee: 9.9,
  restPeriodFeeTotalAmount: 9.9,
  restPeriodNextContributionAmount: 59.9,
  "reminder.openItem.amount": 49.9,
  "reminder.principalAmount": 49.9,
  "reminder.feeAmount": 5,
  "reminder.totalAmount": 54.9,
  "reminder.regularOpenAmount": 49.9,
  "reminder.chargebackOpenAmount": 39.9,
  "reminder.chargebackFeeOpenAmount": 5,
  "reminder.personTotalOpenAmount": 89.8,
  "debtCollection.totalAmount": 139.7,
};

/**
 * Aufstellungen: Bezeichnung, Betrag und Zusatz stehen je Zeile nebeneinander, getrennt wie im
 * erzeugten Text. Der Betrag ist auch hier eine Zahl.
 */
const TEMPLATE_VARIABLE_PREVIEW_ITEM_LISTS: Record<Lang, Record<string, Array<Array<string | number>>>> = {
  de: {
    contractItems: [
      ["Mitgliedschaft Premium", 59.9, "monatlich"],
      ["Anteiliger Beitrag", 19.97, "einmalig"],
      ["Startpaket", 49, "einmalig"],
      ["Verwaltungsgebühr", 12, "einmalig"],
    ],
    "debtCollection.openItems": [
      ["01.05.2026", "Mitgliedsbeitrag Mai", 49.9],
      ["15.05.2026", "Rücklastschriftgebühr", 39.9],
      ["01.06.2026", "Mitgliedsbeitrag Juni", 49.9],
    ],
  },
  en: {
    contractItems: [
      ["Premium membership", 59.9, "monthly"],
      ["Pro-rata fee", 19.97, "one-off"],
      ["Starter package", 49, "one-off"],
      ["Administration fee", 12, "one-off"],
    ],
    "debtCollection.openItems": [
      ["01 May 2026", "Membership fee May", 49.9],
      ["15 May 2026", "Chargeback fee", 39.9],
      ["01 Jun 2026", "Membership fee June", 49.9],
    ],
  },
};

const TEMPLATE_VARIABLE_PREVIEW_VALUES: Record<Lang, TemplateVariablePreviewValueSet> = {
  de: {
    title: "Herr",
    firstName: "Max",
    lastName: "Mustermann",
    email: "max.mustermann@example.com",
    phone: "+49 30 123456",
    mobile: "+49 170 1234567",
    street: "Musterstraße 12",
    zip: "10115",
    city: "Berlin",
    country: "Deutschland",
    dateOfBirth: "14.05.1990",
    greeting: "Hallo",
    greetingFirstName: "Hallo Max",
    greetingLastName: "Hallo Herr Mustermann",
    salutationDearFirstName: "Lieber Max",
    salutationDearLastName: "Sehr geehrter Herr Mustermann",
    salutationFormalFull: "Sehr geehrter Herr Mustermann",
    duSie: "Sie",
    dirIhnen: "Ihnen",
    dichSie: "Sie",
    deinIhr: "Ihr",
    deineIhre: "Ihre",
    bankAccountHolder: "Max Mustermann",
    iban: "DE89 3704 0044 0532 0130 00",
    bic: "COBADEFFXXX",
    mandateReference: "MND-2026-000123",
    mandateSignedDate: "01.06.2026",
    studioName: "m1 Fitness Berlin Mitte",
    studioShortName: "Berlin Mitte",
    studioCode: "BER-MITTE",
    studioStreet: "Studioallee 5",
    studioZip: "10115",
    studioCity: "Berlin",
    studioCountry: "Deutschland",
    studioPhone: "+49 30 987654",
    studioEmail: "berlin-mitte@example.com",
    studioWebsite: "https://studio.example.com",
    studioCostCenter: "KST-1001",
    studioBankAccountHolder: "m1 Fitness GmbH",
    studioIban: "DE12 1001 0010 0123 4567 89",
    studioBic: "PBNKDEFFXXX",
    studioAddress: "Musterstraße 12, 10115 Berlin",
    companyName: "RD Sport eGbR",
    companyStreet: "Musterstraße 1",
    companyZip: "10115",
    companyCity: "Berlin",
    companyAddress: "Musterstraße 1, 10115 Berlin",
    companyPhone: "+49 30 123456",
    companyEmail: "info@example.com",
    companyWebsite: "www.example.com",
    companyRepresentatives: "Ronny Schulz, Diana Schulz",
    companyRepresentativesLabel: "Gesellschafter",
    companySupervisoryBoardChair: "",
    companyRegisterCourt: "Amtsgericht Berlin",
    companyRegisterNumber: "GsR 1234 B",
    companyRegister: "Amtsgericht Berlin GsR 1234 B",
    companyTaxNumber: "12/345/67890",
    companyVatId: "DE123456789",
    contractPlanName: "Premium 12 Monate",
    contractStartDate: "01.07.2026",
    contractEndDate: "30.06.2027",
    contractStatus: "aktiv",
    contractBillingCycle: "monatlich",
    contractTerm: "12 Monate",
    contractTermValue: "12",
    contractTermUnit: "Monate",
    cancellationDate: "15.06.2026",
    cancellationReason: "Umzug",
    cancellationNote: "Kündigung schriftlich bestätigt.",
    cancellationProcessedBy: "Sabine Beispiel",
    restPeriodRequestDate: "20.05.2026",
    restPeriodStartDate: "01.08.2026",
    restPeriodEndDate: "31.08.2026",
    restPeriodNextContributionDueDate: "01.09.2026",
    restPeriodWeeksCount: "4",
    restPeriodNote: "Ruhezeit wegen Urlaub.",
    currentDate: "04.06.2026",
    currentTime: "10:30",
    currentDateTime: "04.06.2026, 10:30",
    currentYear: "2026",
    currentMonth: "Juni",
    currentDay: "4",
    appointmentStartTime: "12.06.2026, 10:00",
    appointmentEndTime: "12.06.2026, 10:45",
    appointmentDate: "12.06.2026",
    appointmentTime: "10:00",
    appointmentName: "Probetraining",
    appointmentTypeName: "Probetraining",
    appointmentDurationMinutes: "45",
    bookingReference: "TERM-2026-0042",
    guestFirstName: "Anna",
    guestLastName: "Beispiel",
    cancelBookingUrl: "https://studio.example.com/buchung/stornieren/B-2026-0042",
    editBookingUrl: "https://studio.example.com/buchung/bearbeiten/B-2026-0042",
    bookingConfirmationCode: "48391",
    "reminder.openItem.dueDate": "31.05.2026",
    "reminder.openItem.description": "Mitgliedsbeitrag Juni 2026",
    "debtCollection.agency.name": "Inkasso Muster GmbH",
    "debtCollection.agency.street": "Musterstraße 12",
    "debtCollection.agency.zip": "10115",
    "debtCollection.agency.city": "Berlin",
    "debtCollection.agency.country": "Deutschland",
    "debtCollection.agency.contactPerson": "Frau Schneider",
    "debtCollection.agency.email": "faelle@inkasso-muster.example.com",
    "debtCollection.agency.phone": "+49 30 1234567",
    "debtCollection.agency.website": "https://inkasso-muster.example.com",
    "debtCollection.agency.customerNumber": "KD-99123",
    "debtCollection.handover.transmissionReference": "DC-2026-000142",
    "debtCollection.case.externalReference": "AZ 4711/26",
    "debtCollection.openItemCount": "3",
    BookingWidgetUrl: "https://studio.example.com/book/berlin-mitte",
    TerminBuchungLink: "https://studio.example.com/book/berlin-mitte",
    resetLink: "https://app.example.com/passwort-zuruecksetzen/abc123",
    displayName: "Max Mustermann",
    username: "max.mustermann",
    otpCode: "284913",
    startTime: "12.06.2026, 10:00",
    endTime: "12.06.2026, 10:45"
  },
  en: {
    title: "Mr.",
    firstName: "John",
    lastName: "Sample",
    email: "john.sample@example.com",
    phone: "+49 30 123456",
    mobile: "+49 170 1234567",
    street: "Sample Street 12",
    zip: "10115",
    city: "Berlin",
    country: "Germany",
    dateOfBirth: "14 May 1990",
    greeting: "Hello",
    greetingFirstName: "Hello John",
    greetingLastName: "Hello Mr. Sample",
    salutationDearFirstName: "Dear John",
    salutationDearLastName: "Dear Mr. Sample",
    salutationFormalFull: "Dear Mr. Sample",
    duSie: "you",
    dirIhnen: "you",
    dichSie: "you",
    deinIhr: "your",
    deineIhre: "your",
    bankAccountHolder: "John Sample",
    iban: "DE89 3704 0044 0532 0130 00",
    bic: "COBADEFFXXX",
    mandateReference: "MND-2026-000123",
    mandateSignedDate: "1 June 2026",
    studioName: "m1 Fitness Berlin Mitte",
    studioShortName: "Berlin Mitte",
    studioCode: "BER-MITTE",
    studioStreet: "Studio Avenue 5",
    studioZip: "10115",
    studioCity: "Berlin",
    studioCountry: "Germany",
    studioPhone: "+49 30 987654",
    studioEmail: "berlin-mitte@example.com",
    studioWebsite: "https://studio.example.com",
    studioCostCenter: "CC-1001",
    studioBankAccountHolder: "m1 Fitness GmbH",
    studioIban: "DE12 1001 0010 0123 4567 89",
    studioBic: "PBNKDEFFXXX",
    studioAddress: "Sample Street 12, 10115 Berlin",
    companyName: "RD Sport eGbR",
    companyStreet: "Sample Street 1",
    companyZip: "10115",
    companyCity: "Berlin",
    companyAddress: "Sample Street 1, 10115 Berlin",
    companyPhone: "+49 30 123456",
    companyEmail: "info@example.com",
    companyWebsite: "www.example.com",
    companyRepresentatives: "Ronny Schulz, Diana Schulz",
    companyRepresentativesLabel: "Partners",
    companySupervisoryBoardChair: "",
    companyRegisterCourt: "Local court Berlin",
    companyRegisterNumber: "GsR 1234 B",
    companyRegister: "Local court Berlin GsR 1234 B",
    companyTaxNumber: "12/345/67890",
    companyVatId: "DE123456789",
    contractPlanName: "Premium 12 months",
    contractStartDate: "1 July 2026",
    contractEndDate: "30 June 2027",
    contractStatus: "active",
    contractBillingCycle: "monthly",
    contractTerm: "12 months",
    contractTermValue: "12",
    contractTermUnit: "Monate",
    cancellationDate: "15 June 2026",
    cancellationReason: "Relocation",
    cancellationNote: "Cancellation confirmed in writing.",
    cancellationProcessedBy: "Sarah Example",
    restPeriodRequestDate: "20 May 2026",
    restPeriodStartDate: "1 August 2026",
    restPeriodEndDate: "31 August 2026",
    restPeriodNextContributionDueDate: "1 September 2026",
    restPeriodWeeksCount: "4",
    restPeriodNote: "Rest period for vacation.",
    currentDate: "4 June 2026",
    currentTime: "10:30",
    currentDateTime: "4 June 2026, 10:30",
    currentYear: "2026",
    currentMonth: "June",
    currentDay: "4",
    appointmentStartTime: "12 June 2026, 10:00",
    appointmentEndTime: "12 June 2026, 10:45",
    appointmentDate: "12 June 2026",
    appointmentTime: "10:00",
    appointmentName: "Trial training",
    appointmentTypeName: "Trial training",
    appointmentDurationMinutes: "45",
    bookingReference: "APT-2026-0042",
    guestFirstName: "Anna",
    guestLastName: "Example",
    cancelBookingUrl: "https://studio.example.com/booking/cancel/B-2026-0042",
    editBookingUrl: "https://studio.example.com/booking/edit/B-2026-0042",
    bookingConfirmationCode: "48391",
    "reminder.openItem.dueDate": "31 May 2026",
    "reminder.openItem.description": "Membership fee June 2026",
    "debtCollection.agency.name": "Sample Collections Ltd.",
    "debtCollection.agency.street": "12 Sample Street",
    "debtCollection.agency.zip": "10115",
    "debtCollection.agency.city": "Berlin",
    "debtCollection.agency.country": "Germany",
    "debtCollection.agency.contactPerson": "Ms Schneider",
    "debtCollection.agency.email": "cases@sample-collections.example.com",
    "debtCollection.agency.phone": "+49 30 1234567",
    "debtCollection.agency.website": "https://sample-collections.example.com",
    "debtCollection.agency.customerNumber": "CU-99123",
    "debtCollection.handover.transmissionReference": "DC-2026-000142",
    "debtCollection.case.externalReference": "Ref 4711/26",
    "debtCollection.openItemCount": "3",
    BookingWidgetUrl: "https://studio.example.com/book/berlin-mitte",
    TerminBuchungLink: "https://studio.example.com/book/berlin-mitte",
    resetLink: "https://app.example.com/reset-password/abc123",
    displayName: "John Sample",
    username: "john.sample",
    otpCode: "284913",
    startTime: "12 June 2026, 10:00",
    endTime: "12 June 2026, 10:45"
  }
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parsePreviewDate(value: string): Date | null {
  const deMatch = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (deMatch) {
    const date = new Date(Number(deMatch[3]), Number(deMatch[2]) - 1, Number(deMatch[1]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatPreviewDate(value: Date, sourceValue: string): string {
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(sourceValue)) {
    const day = String(value.getDate()).padStart(DATE_PART_WIDTH, "0");
    const month = String(value.getMonth() + 1).padStart(DATE_PART_WIDTH, "0");
    return `${day}.${month}.${value.getFullYear()}`;
  }

  // display-format: Vorschau übernimmt das Format des Vorlagentexts, nicht das Benutzerformat.
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" }).format(value);
}

function parsePreviewNumber(value: string): number | null {
  const cleaned = value.trim().replace(/[^\d,.\-+]/g, "");
  const comma = cleaned.lastIndexOf(",");
  const dot = cleaned.lastIndexOf(".");
  let normalized: string;
  if (comma >= 0 && dot >= 0) {
    const decimalSeparator = comma > dot ? "," : ".";
    const groupSeparator = decimalSeparator === "," ? "." : ",";
    normalized = cleaned.split(groupSeparator).join("");
    if (decimalSeparator === ",") normalized = normalized.replace(",", ".");
  } else if (comma >= 0) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = cleaned.replace(/,/g, "");
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatPreviewNumber(value: number, leftValue: string): string {
  const usesComma = leftValue.replace(/[^\d,.]/g, "").lastIndexOf(",") > leftValue.replace(/[^\d,.]/g, "").lastIndexOf(".");
  const hasCurrencyPrefix = /^\s*€/.test(leftValue);
  const hasCurrencySuffix = /€\s*$/.test(leftValue);
  const sourceDecimalPlaces = decimalPlacesOf(leftValue);
  const decimalPlaces = sourceDecimalPlaces > 0 ? sourceDecimalPlaces : Number.isInteger(value) ? 0 : DEFAULT_FRACTION_DIGITS;
  // display-format: Vorschau übernimmt das Zahlenformat des Vorlagentexts.
  const rendered = value.toFixed(decimalPlaces).replace(".", usesComma ? "," : ".");
  if (hasCurrencyPrefix) return `€${rendered}`;
  if (hasCurrencySuffix) return `${rendered} €`;
  return rendered;
}

function decimalPlacesOf(value: string): number {
  const trimmed = value.trim().replace(/%/g, "").replace(/[^\d,.]/g, "");
  const separator = Math.max(trimmed.lastIndexOf(","), trimmed.lastIndexOf("."));
  if (separator < 0) return 0;
  const places = trimmed.length - separator - 1;
  return places <= MAX_SOURCE_DECIMAL_PLACES ? places : 0;
}

function replaceCalculationTokens(input: string, previewValues: TemplateVariablePreviewValueSet): string {
  const pattern = /(\{\{\s*)([A-Za-z][A-Za-z0-9_]*)\s+([+\-*/])\s+([A-Za-z][A-Za-z0-9_]*|[-+]?\d+(?:[,.]\d+)?\s*%?|[-+]?\d+\s*[dDwWmMyY])(\s*\}\})/g;
  return input.replace(pattern, (token, _start, leftKey: string, op: string, rightRaw: string) => {
    const leftValue = previewValues[leftKey];
    if (!leftValue) return token;

    const leftDate = parsePreviewDate(leftValue);
    const dateOffset = String(rightRaw).trim().match(/^([-+]?\d+)\s*([dDwWmMyY])$/);
    if (leftDate && dateOffset && (op === CALC_OP_ADD || op === CALC_OP_SUBTRACT)) {
      const amount = Number(dateOffset[1]) * (op === CALC_OP_SUBTRACT ? -1 : 1);
      const unit = dateOffset[2].toLowerCase();
      const result = new Date(leftDate);
      if (unit === DATE_UNIT_DAY) result.setDate(result.getDate() + amount);
      else if (unit === DATE_UNIT_WEEK) result.setDate(result.getDate() + amount * DAYS_PER_WEEK);
      else if (unit === DATE_UNIT_MONTH) result.setMonth(result.getMonth() + amount);
      else if (unit === DATE_UNIT_YEAR) result.setFullYear(result.getFullYear() + amount);
      else return token;
      return formatPreviewDate(result, leftValue);
    }

    const leftNumber = parsePreviewNumber(leftValue);
    if (leftNumber == null) return token;

    const rightText = String(rightRaw).trim();
    const rightIsPercent = rightText.endsWith("%");
    const rightValue = previewValues[rightText] ?? rightText;
    const rightNumberSource = rightIsPercent ? rightText.slice(0, -1) : rightValue;
    const parsedRight = parsePreviewNumber(rightNumberSource);
    if (parsedRight == null) return token;

    const effectiveRight = rightIsPercent ? leftNumber * parsedRight / PERCENT_SCALE : parsedRight;
    let result: number;
    if (op === CALC_OP_ADD) result = leftNumber + effectiveRight;
    else if (op === CALC_OP_SUBTRACT) result = leftNumber - effectiveRight;
    else if (op === CALC_OP_MULTIPLY) result = rightIsPercent ? effectiveRight : leftNumber * effectiveRight;
    else if (op === CALC_OP_DIVIDE && effectiveRight !== 0) result = leftNumber / effectiveRight;
    else return token;

    return formatPreviewNumber(result, leftValue);
  });
}

/**
 * Word-Zeilenumbruch. Mehrzeilige Werte (z. B. die Positionsliste eines Vertragspakets) müssen im
 * Dokument als Umbruch ankommen; ein rohes \n bliebe im Textlauf eines SFDT-Absatzes wirkungslos.
 * Das Backend setzt beim Versand denselben Umbruch ein.
 */
const DOCUMENT_LINE_BREAK = "\u000B";

function withLineBreaks(value: string, lineBreak: string): string {
  return lineBreak === LINE_BREAK_LF ? value : value.replace(/\r\n|\r|\n/g, lineBreak);
}

function replaceVariableTokens(
  input: string,
  previewValues: TemplateVariablePreviewValueSet,
  lineBreak = "\n"
): string {
  let next = replaceCalculationTokens(input, previewValues);
  next = next.replace(
    TEMPLATE_LINK_TOKEN_REGEX,
    (token, _start, key: string, doubleQuotedLabel: string | undefined, singleQuotedLabel: string | undefined, htmlQuotedLabel: string | undefined) =>
      previewValues[key] ? (doubleQuotedLabel ?? singleQuotedLabel ?? htmlQuotedLabel ?? previewValues[key]) : token
  );
  for (const [key, value] of Object.entries(previewValues)) {
    const escapedKey = escapeRegExp(key);
    next = next.replace(new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, "gi"), withLineBreaks(value, lineBreak));
  }
  return next;
}

function buildPreviewLinkTextInline(displayText: string, baseInline: Record<string, unknown>): Record<string, unknown> {
  const characterFormat = typeof baseInline.characterFormat === "object" && baseInline.characterFormat
    ? baseInline.characterFormat
    : {};
  return {
    ...baseInline,
    characterFormat: {
      ...characterFormat,
      underline: "Single",
      fontColor: "#0563c1",
      bidi: false
    },
    text: displayText
  };
}

function replaceInlineLinkTokens(
  inline: Record<string, unknown>,
  previewValues: TemplateVariablePreviewValueSet
): Array<Record<string, unknown>> | null {
  const text = typeof inline.text === "string" ? inline.text : "";
  if (!text || !TEMPLATE_LINK_TOKEN_REGEX.test(text)) {
    TEMPLATE_LINK_TOKEN_REGEX.lastIndex = 0;
    return null;
  }

  TEMPLATE_LINK_TOKEN_REGEX.lastIndex = 0;
  const result: Array<Record<string, unknown>> = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = TEMPLATE_LINK_TOKEN_REGEX.exec(text)) !== null) {
    const [token, , key, doubleQuotedLabel, singleQuotedLabel, htmlQuotedLabel] = match;
    const hrefValue = previewValues[key];
    if (!hrefValue) continue;

    const before = text.slice(cursor, match.index);
    if (before) {
      result.push({ ...inline, text: replaceVariableTokens(before, previewValues) });
    }

    const displayText = doubleQuotedLabel ?? singleQuotedLabel ?? htmlQuotedLabel ?? hrefValue;
    result.push(buildPreviewLinkTextInline(displayText, inline));
    cursor = match.index + token.length;
  }

  const after = text.slice(cursor);
  if (after) {
    result.push({ ...inline, text: replaceVariableTokens(after, previewValues) });
  }

  return result;
}

function isPreviewLinkTextInline(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const characterFormat = record.characterFormat;
  if (!characterFormat || typeof characterFormat !== "object" || Array.isArray(characterFormat)) return false;
  const format = characterFormat as Record<string, unknown>;
  return typeof record.text === "string" &&
    format.underline === SFDT_UNDERLINE_SINGLE &&
    String(format.fontColor || "").toLowerCase() === SFDT_LINK_FONT_COLOR;
}

function removeRepeatedPreviewLinkText(value: unknown): unknown {
  if (!isPreviewLinkTextInline(value)) return value;
  const record = value as Record<string, unknown>;
  const text = String(record.text || "");
  if (text.length < REPEATED_TEXT_COPIES || text.length % REPEATED_TEXT_COPIES !== 0) return value;
  const half = text.slice(0, text.length / REPEATED_TEXT_COPIES);
  if (half && half === text.slice(text.length / REPEATED_TEXT_COPIES)) {
    return { ...record, text: half };
  }
  return value;
}

function getInlineText(value: unknown): string {
  return value && typeof value === "object" && !Array.isArray(value) && typeof (value as Record<string, unknown>).text === "string"
    ? String((value as Record<string, unknown>).text)
    : "";
}

function removeDuplicateAdjacentPreviewLinkTextInlines(inlines: unknown[]): unknown[] {
  const result: unknown[] = [];
  for (const rawInline of inlines) {
    const inline = removeRepeatedPreviewLinkText(rawInline);
    const previous = result[result.length - 1];
    const previousText = getInlineText(previous);
    const currentText = getInlineText(inline);
    if (
      previousText &&
      previousText === currentText &&
      (isPreviewLinkTextInline(previous) || isPreviewLinkTextInline(inline))
    ) {
      continue;
    }
    result.push(inline);
  }
  return result;
}

function replaceVariableTokensDeep(
  value: unknown,
  previewValues: TemplateVariablePreviewValueSet,
  options: TemplateVariablePreviewOptions
): unknown {
  if (typeof value === "string") {
    return replaceVariableTokens(value, previewValues, DOCUMENT_LINE_BREAK);
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceVariableTokensDeep(item, previewValues, options));
  }
  if (value && typeof value === "object") {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(source)) {
      if (options.renderLinks && key === SFDT_INLINES_KEY && Array.isArray(child)) {
        const replacedInlines = child.flatMap((inline) => {
          if (!inline || typeof inline !== "object" || Array.isArray(inline)) {
            return [replaceVariableTokensDeep(inline, previewValues, options)];
          }
          const replacement = replaceInlineLinkTokens(inline as Record<string, unknown>, previewValues);
          if (replacement) return replacement;
          return [replaceVariableTokensDeep(inline, previewValues, options)];
        });
        result[key] = removeDuplicateAdjacentPreviewLinkTextInlines(replacedInlines);
        continue;
      }
      result[key] = replaceVariableTokensDeep(child, previewValues, options);
    }
    return result;
  }
  return value;
}

/** Trennt Bezeichnung, Betrag und Zusatz innerhalb einer Zeile — wie die Aufstellung im Schreiben. */
const ITEM_PART_SEPARATOR = " · ";

/**
 * Die Beispielwerte, die Geld zeigen. Sie entstehen hier und nicht in der Tabelle oben, damit die
 * Leiste denselben Betrag zeigt wie das erzeugte Schreiben: beides über `formatMoney` und die
 * Einstellung unter Einheiten.
 */
function moneyPreviewValues(lang: Lang, units?: UnitsSettings | null): TemplateVariablePreviewValueSet {
  const result: TemplateVariablePreviewValueSet = {};
  for (const [key, amount] of Object.entries(TEMPLATE_VARIABLE_PREVIEW_AMOUNTS)) {
    result[key] = formatMoney(amount, units);
  }
  for (const [key, rows] of Object.entries(TEMPLATE_VARIABLE_PREVIEW_ITEM_LISTS[lang])) {
    result[key] = rows
      .map((parts) => parts
        .map((part) => (typeof part === "number" ? formatMoney(part, units) : part))
        .join(ITEM_PART_SEPARATOR))
      .join("\n");
  }
  return result;
}

export function buildTemplateVariablePreviewValues(
  variables: readonly TemplateVariable[],
  lang: Lang,
  overrides: TemplateVariablePreviewValueSet = {},
  units?: UnitsSettings | null
): TemplateVariablePreviewValueSet {
  const base = TEMPLATE_VARIABLE_PREVIEW_VALUES[lang];
  const money = moneyPreviewValues(lang, units);
  const fallbackPrefix = lang === "de" ? "Beispielwert" : "Sample value";
  const result: TemplateVariablePreviewValueSet = {};

  for (const variable of variables) {
    const override = overrides[variable.key]?.trim();
    result[variable.key] = override
      || money[variable.key]
      || base[variable.key]
      || `${fallbackPrefix}: ${textFor(lang, variable.label)}`;
  }

  return result;
}

export function replaceTemplateVariablesWithPreviewValues(
  value: string,
  previewValues: TemplateVariablePreviewValueSet,
  options: TemplateVariablePreviewOptions = {}
): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) return value;

  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.stringify(replaceVariableTokensDeep(JSON.parse(value), previewValues, options));
    } catch {
      return replaceVariableTokens(value, previewValues, DOCUMENT_LINE_BREAK);
    }
  }

  return replaceVariableTokens(value, previewValues);
}
